// IndexedDB upload queue for field captures (2026-10-02).
//
// Every capture is written here FIRST, then uploaded. iOS has no Background
// Sync, so the queue drains only while the app is open: on enqueue, on the
// `online` event, when the app comes back to the foreground, and on a 30 s
// timer. A capture leaves the queue only when the server acknowledged it
// (2xx), or the server rejected it for good (4xx other than 401/408/429 -
// shown as "needs attention" and kept until the user discards it). The
// server is idempotent on client_uuid, so a retry after a lost response
// never duplicates. navigator.storage.persist() is requested so the browser
// doesn't evict queued photos (home-screen apps on iOS are exempt from
// Safari's 7-day storage cap).
//
// 2026-10-19 (row #40): every request has a timeout, a run that spans a
// suspension is restarted, Android drains with the app closed (Background
// Sync, sw.js), and uploadBadge.ts shows what is waiting, with "Send now".

// the upload loop, shared with the service worker (row #40): sets globalThis.WrapOutboxCore
import "../../public/outbox-core.js";

/** public/outbox-core.js */
export interface DrainResult {
  sent: number;
  rejected: number;
  waiting: number;
  stopped: string | null;
  busy?: boolean;
}
export interface WrapOutboxCore {
  SYNC_TAG: string;
  order<T extends { created: number; kind?: string }>(items: T[]): T[];
  timeoutMs(it: { photos?: Blob[]; json?: unknown }): number;
  drainOnce(deps: {
    list(): Promise<OutboxItem[]>;
    put(it: OutboxItem): Promise<void>;
    del(client_uuid: string): Promise<void>;
    fetch(url: string, init: RequestInit): Promise<Response>;
    owner: string | null;
    strictOwner?: boolean;
    signal?: AbortSignal;
    onUploaded?(record: unknown, it: OutboxItem): void;
    onChange?(): void;
    now?(): number;
    setTimeout?(fn: () => void, ms: number): unknown;
    clearTimeout?(t: unknown): void;
  }): Promise<DrainResult>;
  withLock(locks: unknown, fn: () => Promise<DrainResult>): Promise<DrainResult>;
}

const DB_NAME = "wrap-field";
const DB_VERSION = 4; // 2: + "packs" (offline basemap packs, packs.ts); 3: + "areaTiles" (areaTileStore.ts); 4: + MSQA forms / photo previews (msqa.ts)
export const OUTBOX = "outbox";
export const AREAS = "areas";
export const PACKS = "packs";
export const AREA_TILES = "areaTiles";
export const MSQA_FORMS = "msqaForms";
export const MSQA_PHOTOS = "msqaPhotos";

export interface OutboxItem {
  client_uuid: string;
  meta: Record<string, unknown>;
  photos: Blob[];
  photo_headings: (number | null)[];
  created: number;
  attempts: number;
  last_error: string | null;
  failed: boolean; // permanently rejected by the server
  owner: string | null; // username that captured it - only uploaded under that login (shared phones)
  server_id?: number;
  last_attempt_at?: number;
  /** A JSON request instead of a capture upload (MSQA form autosave / submit,
   * 2026-10-13): sent as `method` `endpoint` with this body. The client_uuid
   * is the item key, so a newer autosave of the same form replaces the queued
   * one. Kept in order with the photo uploads: a submit queued after its
   * photos uploads after them. */
  endpoint?: string;
  method?: "PUT" | "POST";
  json?: unknown;
  /** A multipart upload to another endpoint (AR scan-mode frames, 2026-10-14):
   * `meta` + each blob of `photos` under `file_field`, POSTed here instead of
   * /api/field/captures. */
  upload_endpoint?: string;
  file_field?: string;
  /** "scan_frame" / "scan_session": grouped in the "Waiting to upload" list
   * and counted by the scan-mode indicator (scan.ts) */
  kind?: string;
  /** the scan session's client_uuid, for kind scan_* */
  group?: string;
}

let currentOwner: string | null = null;
/** The signed-in username: queued captures of another user are never
 * uploaded under this login (they wait until their owner signs in). */
export function setOutboxOwner(username: string | null): void {
  currentOwner = username;
}
export function outboxOwner(): string | null {
  return currentOwner;
}

let dbp: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (dbp) return dbp;
  dbp = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(OUTBOX)) db.createObjectStore(OUTBOX, { keyPath: "client_uuid" });
      if (!db.objectStoreNames.contains(AREAS)) db.createObjectStore(AREAS, { keyPath: "key" });
      if (!db.objectStoreNames.contains(PACKS)) db.createObjectStore(PACKS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(AREA_TILES)) db.createObjectStore(AREA_TILES, { keyPath: "key" });
      if (!db.objectStoreNames.contains(MSQA_FORMS)) db.createObjectStore(MSQA_FORMS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(MSQA_PHOTOS)) db.createObjectStore(MSQA_PHOTOS, { keyPath: "ref" });
    };
    req.onsuccess = () => {
      const db = req.result;
      // a newer tab upgrading the schema: let it (this tab reopens on next use)
      db.onversionchange = () => {
        db.close();
        dbp = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      dbp = null;
      reject(req.error);
    };
  });
  return dbp;
}

export function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        t.oncomplete = () => resolve(req ? (req.result as T) : undefined);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
      }),
  );
}

export function newUuid(): string {
  const c = crypto as Crypto & { randomUUID?: () => string };
  if (typeof c.randomUUID === "function") return c.randomUUID().replace(/-/g, "");
  return Array.from(c.getRandomValues(new Uint8Array(16)), (b: number) => b.toString(16).padStart(2, "0")).join("");
}

const listeners = new Set<() => void>();
export function onOutboxChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function changed(): void {
  for (const fn of listeners) fn();
}

export async function listOutbox(): Promise<OutboxItem[]> {
  return ((await tx<OutboxItem[]>(OUTBOX, "readonly", (s) => s.getAll())) ?? []).sort((a, b) => a.created - b.created);
}

export async function enqueue(item: Omit<OutboxItem, "created" | "attempts" | "last_error" | "failed" | "owner">): Promise<void> {
  await tx(OUTBOX, "readwrite", (s) =>
    s.put({ ...item, owner: currentOwner, created: Date.now(), attempts: 0, last_error: null, failed: false }));
  changed();
  void drain();
}

export async function discard(client_uuid: string): Promise<void> {
  await tx(OUTBOX, "readwrite", (s) => s.delete(client_uuid));
  changed();
}

export async function retry(client_uuid: string): Promise<void> {
  const items = await listOutbox();
  const it = items.find((i) => i.client_uuid === client_uuid);
  if (!it) return;
  await tx(OUTBOX, "readwrite", (s) => s.put({ ...it, failed: false, last_error: null }));
  changed();
  void drain();
}

const uploadedListeners = new Set<(serverRecord: unknown, item: OutboxItem) => void>();
export function onUploaded(fn: (serverRecord: unknown, item: OutboxItem) => void): () => void {
  uploadedListeners.add(fn);
  return () => uploadedListeners.delete(fn);
}

/** Discard every queued item of a scan session (or every scan item). */
export async function discardKind(kind: string, group?: string): Promise<number> {
  const items = (await listOutbox()).filter((i) => i.kind === kind && (group === undefined || i.group === group));
  for (const i of items) await tx(OUTBOX, "readwrite", (s) => s.delete(i.client_uuid));
  if (items.length) changed();
  return items.length;
}

// --- draining (row #40, 2026-10-19) -------------------------------------------
// The upload loop itself is public/outbox-core.js, shared with the service
// worker's Background Sync. This page part decides WHEN to run it: on
// enqueue, at start, on `online`, `pageshow` and coming back to the
// foreground, on a 30 s timer, sooner after a failure (5, 10, 20, 30 s), and
// on "Send now". A run that was in flight when the page was hidden is aborted
// on return and started again: iOS freezes a backgrounded app, and a request
// frozen that way may never settle (the 15 Oct frames waited for days).

interface Run {
  ctrl: AbortController;
  startedAt: number;
  done: Promise<void>;
}
let run: Run | null = null;
let hiddenAt = 0;
let failures = 0;
let again = false;
let soon: number | null = null;

/** What the upload badges show (uploadBadge.ts). */
export interface OutboxStatus {
  waiting: number; // still to upload (this login's and unowned items)
  scanFrames: number; // of which AR scan frames
  rejected: number; // refused by the server: needs attention
  otherOwner: number; // captured by another login on this phone
  sending: boolean;
  lastError: string | null; // what stopped the queue last (cleared by a successful upload)
  lastErrorAt: number | null;
  lastSentAt: number | null;
  backgroundSync: boolean; // the browser can finish uploads with the app closed (Android Chrome)
}
const ST = { lastError: null as string | null, lastErrorAt: null as number | null, lastSentAt: null as number | null, backgroundSync: false };

export async function outboxStatus(): Promise<OutboxStatus> {
  const items = await listOutbox();
  const others = (i: OutboxItem) => !!(i.owner && currentOwner && i.owner !== currentOwner);
  const live = items.filter((i) => !i.failed && !others(i));
  return {
    waiting: live.length,
    scanFrames: live.filter((i) => i.kind === "scan_frame").length,
    rejected: items.filter((i) => i.failed && !others(i)).length,
    otherOwner: items.filter(others).length,
    sending: run !== null,
    lastError: live.length ? ST.lastError : null,
    lastErrorAt: live.length ? ST.lastErrorAt : null,
    lastSentAt: ST.lastSentAt,
    backgroundSync: ST.backgroundSync,
  };
}

function core(): WrapOutboxCore {
  return (globalThis as unknown as { WrapOutboxCore: WrapOutboxCore }).WrapOutboxCore;
}

function scheduleSoon(ms: number): void {
  if (soon !== null) window.clearTimeout(soon);
  soon = window.setTimeout(() => {
    soon = null;
    void drain();
  }, ms);
}

/** Ask the service worker to finish the queue when there is signal, even with
 * the app closed (Android Chrome; a no-op on iOS, which has no Background Sync). */
async function requestBackgroundSync(): Promise<void> {
  try {
    const reg = (await navigator.serviceWorker?.getRegistration()) as (ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } }) | undefined;
    if (!reg?.sync || !reg.active) return;
    await reg.sync.register(`${core().SYNC_TAG}:${currentOwner ?? ""}`);
    ST.backgroundSync = true;
  } catch {
    /* not supported, or refused (no permission) */
  }
}

/** Upload everything queued (one at a time, oldest first). `force` ("Send
 * now"): also when the browser says it's offline, and abort a run in flight
 * first (it may be a request frozen by a suspension). */
export async function drain(opts: { force?: boolean } = {}): Promise<void> {
  if (run) {
    if (!opts.force && run.startedAt >= hiddenAt) {
      again = true; // a live run: look again when it ends (it listed the queue before this item)
      return;
    }
    const old = run;
    old.ctrl.abort();
    await old.done;
  }
  if (run) return; // another caller restarted it while we waited
  if (!opts.force && !navigator.onLine) return;
  const ctrl = new AbortController();
  let finish = () => {};
  const mine: Run = { ctrl, startedAt: Date.now(), done: new Promise<void>((r) => (finish = r)) };
  run = mine;
  again = false;
  changed();
  let rerun = false;
  try {
    const locks = (navigator as Navigator & { locks?: unknown }).locks;
    const res = await core().withLock(locks, () => core().drainOnce({
      list: listOutbox,
      put: (it) => tx(OUTBOX, "readwrite", (s) => s.put(it)).then(() => undefined),
      del: (id) => tx(OUTBOX, "readwrite", (s) => s.delete(id)).then(() => undefined),
      fetch: (url, init) => fetch(url, init),
      owner: currentOwner,
      signal: ctrl.signal,
      onUploaded: (rec, it) => {
        ST.lastSentAt = Date.now();
        ST.lastError = null;
        ST.lastErrorAt = null;
        for (const fn of uploadedListeners) fn(rec, it as OutboxItem);
      },
      onChange: changed,
    }));
    if (res.busy) {
      scheduleSoon(5_000); // the service worker is uploading: look again shortly
    } else if (res.stopped && res.stopped !== "aborted") {
      ST.lastError = res.stopped;
      ST.lastErrorAt = Date.now();
      failures += 1;
      scheduleSoon(Math.min(30_000, 5_000 * 2 ** (failures - 1)));
      void requestBackgroundSync();
    } else if (!res.stopped) {
      failures = 0;
      rerun = again;
    }
  } catch (e) {
    ST.lastError = `Upload queue error: ${e instanceof Error ? e.message : String(e)}`;
    ST.lastErrorAt = Date.now();
    scheduleSoon(30_000);
  } finally {
    if (run === mine) run = null;
    finish();
    changed();
  }
  if (rerun) void drain();
}

let started = false;
export function startOutbox(): void {
  if (started) return;
  started = true;
  window.addEventListener("online", () => void drain());
  window.addEventListener("pageshow", () => void drain());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      void listOutbox().then((items) => { if (items.some((i) => !i.failed)) void requestBackgroundSync(); }).catch(() => undefined);
    } else {
      void drain(); // aborts and restarts a run that spans the time away
    }
  });
  // the service worker drained the queue (Background Sync): refresh the badges
  navigator.serviceWorker?.addEventListener?.("message", (e: MessageEvent) => {
    if (e.data === "wrap-outbox-changed") changed();
  });
  window.setInterval(() => void drain(), 30_000);
  try {
    void navigator.storage?.persist?.();
  } catch {
    /* not supported */
  }
  void drain();
}
